import Map from "ol/Map";
import View from "ol/View";
import Select from "react-select";
import CircleStyle from "ol/style/Circle";
import { fromLonLat } from "ol/proj";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { Cluster, OSM } from "ol/source";
import { Vector as VectorSource } from "ol/source";
import { Style, Fill, Stroke, Text } from "ol/style";
import { isEmpty } from "lodash";
import { useContext, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { Line, Bar } from "react-chartjs-2";
import { DateInput } from "react-hichestan-datetimepicker";
import { Point } from "ol/geom";
import { Feature } from "ol";
import { useSelector } from "react-redux";

import TooltipTitle from "../../../utils/TooltipTitle/TooltipTitle";
import SiteTitle from "../../../utils/SiteTitle/SiteTitle";
import CardLayout from "../../../utils/CardLayout/CardLayout";
import {
  baseAdminURL,
  dialogContainerStyle,
  initialViewState,
  numberSeparate,
} from "./../../../utils/index";
import { dashboardAdminContext } from "../../../context/Admin/DashboardAdminContext/DashboardAdminContext";
import { toastErrorMessage } from "../../../utils/toastMessage/toastMessage";

import "ol/ol.css";
import _ from "lodash";

const DashboardAdmin = () => {
  const permission = useSelector((state) => state.permission);

  const {
    items,
    charts,
    orders,
    personnels,
    users,
    services,
    handleGetOrders,
    handleGetPersonnels,
    handleGetUsers,
    handleGetServices,
    handleGetCategories,
    addItemToChart,
  } = useContext(dashboardAdminContext);

  useEffect(() => {
    handleGetOrders();
    handleGetPersonnels();
    handleGetUsers();
    handleGetServices();
    handleGetCategories();
  }, []);
  
  return (
    <>
      <SiteTitle title="داشبورد" />

      <div className="mt-2">
        <div className="row">
          <CardItem
            link={baseAdminURL + "/Admin/Orders"}
            title="سفارشات"
            icon="mdi mdi-shopping-outline"
            total={orders?.count}
          />
          <CardItem
            link={baseAdminURL + "/Admin/Personnels"}
            title="پرسنل"
            icon="mdi mdi-account-multiple-check-outline"
            total={personnels?.count}
          />
          <CardItem
            link={baseAdminURL + "/Admin/Users"}
            title="کاربران"
            icon="mdi mdi-account-multiple"
            total={users?.count}
          />
          <CardItem
            link={baseAdminURL + "/Admin/Services"}
            title="خدمات"
            icon="mdi mdi-view-list"
            total={services?.total}
          />
        </div>
      </div>
      {permission.kpi_analysis && (
        <CardLayout>
          <div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const algorithm = e.target.chartSelect?.value;
                addItemToChart(algorithm);
              }}
              className="input-group"
            >
              <select name="chartSelect" className="form-control">
                {items.map((_item) => (
                  <option key={_item.algorithm} value={_item.algorithm}>
                    {_item.title}
                  </option>
                ))}
              </select>
              <div className="input-group-append">
                <button type="submit" className="btn btn-primary">
                  افزودن
                </button>
              </div>
            </form>
          </div>
          {!isEmpty(charts) && (
            <div className="row justify-content-between align-items-center mt-4">
              {charts.map((_item, index) => (
                <div className={_item.className || "col-12"} key={_item.id}>
                  <ChartItem {..._item} index={index} />
                </div>
              ))}
            </div>
          )}
        </CardLayout>
      )}
    </>
  );
};

export default DashboardAdmin;

const CardItem = ({ total, link, title, icon }) => {
  return (
    <div className="col-12 col-sm-6 col-xl-3">
      <div className="card">
        <Link to={link} className="card-body">
          <div className="media">
            <div className="avatar-sm font-size-20 mr-3">
              <span className="avatar-title bg-soft-primary text-primary rounded">
                <i className={icon} />
              </span>
            </div>
            <div className="media-body">
              <div className="font-size-16 mt-2">{title}</div>
            </div>
          </div>
          <h4 className="mt-3 mb-0 primary-font">
            {total ? (
              numberSeparate(total)
            ) : (
              <Spinner variant="primary" animation="border" />
            )}
          </h4>
        </Link>
      </div>
    </div>
  );
};

// Utility to convert raw numbers to Persian words on the axis
const formatBigCurrency = (value) => {
  if (value >= 1_000_000_000_000) {
    return `${(value / 1_000_000_000_000).toFixed(1)} همت`;
  }
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)} میلیارد`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)} میلیون`;
  }
  // If your project uses a helper like numberSeparate, replace this fallback line
  return value.toLocaleString("fa-IR"); 
};

const Chart = ({ type, options, ...otherProps }) => {
  // Define our global human-readable chart configurations
  const globalOptions = {
    responsive: true,
    scales: {
      y: {
        ticks: {
          callback: function (value) {
            return formatBigCurrency(value);
          },
          font: { family: "Vazir, Tahoma, sans-serif" },
        },
      },
      x: {
        ticks: {
          font: { family: "Vazir, Tahoma, sans-serif" },
        },
      },
    },
    plugins: {
      tooltip: {
        rtl: true,
        textDirection: "rtl",
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.y !== null) {
              // Show exact commas on tooltip hover
              label += `${context.parsed.y.toLocaleString("fa-IR")} ریال`;
            }
            return label;
          },
        },
      },
    },
  };

  // Deep merge incoming options with our custom global formatters
  // This ensures specific custom settings from parent views aren't overwritten
  const mergedOptions = _.merge({}, globalOptions, options);

  switch (type) {
    case "Line":
      return <Line options={mergedOptions} {...otherProps} />;
    case "Bar":
      return <Bar options={mergedOptions} {...otherProps} />;
    default:
      return <Line options={mergedOptions} {...otherProps} />;
  }
};

const ChartItem = ({
  title,
  description,
  chartType,
  algorithm,
  items,
  isPeriod,
  isPeriods,
  isDate,
  isService,
  isServiceCategory,
  index,
  isStatus,
  defaultPeriods,
  nextMonth,
}) => {
  const appConfigs = useSelector((state) => state.appConfigs);

  const [data, setData] = useState([]);
  const [periods, setPeriods] = useState(defaultPeriods || []);
  const [unique_id, setUnique_id] = useState("");
  const [service_category_id, setService_category_id] = useState("");
  const [status, setStatus] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [period, setPeriod] = useState(30);
  const [from_date, setFrom_date] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  });
  const [to_date, setTo_date] = useState(() => {
    const date = new Date();
    if (!!nextMonth) date.setMonth(date.getMonth() + 1);
    return date.toISOString().split("T")[0];
  });
  const [isOpen, setIsOpen] = useState(false);

  const {
    categories,
    services,
    handleGetKPIAnalysis,
    handleChartData,
    removeItemFromChart,
  } = useContext(dashboardAdminContext);

  const handleUpdate = () => {
    if (!from_date) return toastErrorMessage("تاریخ شروع الزامی است");
    if (!to_date) return toastErrorMessage("تاریخ پایان الزامی است");

    const objData = {
      from_date: from_date.split("T")[0],
      to_date: to_date.split("T")[0],
      algorithm,
      periods: periods,
      period: +period,
      unique_id,
      status,
      service_category_id,
    };
    handleGetKPIAnalysis(objData, setData);
  };

  useEffect(() => {
    if (isOpen) handleUpdate();
  }, [
    from_date,
    to_date,
    isOpen,
    period,
    periods,
    unique_id,
    status,
    service_category_id,
  ]);

  useEffect(() => {
    if (!isEmpty(data) || data >= 0)
      setChartData(handleChartData(algorithm, data, items, title, periods));
  }, [data]);

  const serviceOptions = services?.list?.map((_value) => ({
    label: _value.name,
    value: _value.unique_id,
  }));

  const categoryOptions = categories?.list?.map((_value) => ({
    label: _value.name,
    value: _value.id,
  }));

  const statusOptions = appConfigs?.STATUS_CHOICES?.split("،")?.map(
    (_value, index) => ({
      label: _value,
      value: index,
    })
  );

  return (
    <div className="card border shadow-lg">
      <div className="row justify-content-between align-items-center card-header">
        <div
          className="col d-flex justify-content-between align-items-center  cursorPointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="h2">{title}</span>{" "}
          <span>
            <i className={`fa fa-angle-${isOpen ? "up" : "down"}  fa-2x`} />
          </span>
        </div>
        <span className="col-auto cursorPointer">
          <i
            className="fa fa-times text-danger fa-2x ml-3"
            onClick={() => removeItemFromChart(index)}
          />
        </span>
      </div>
      {isOpen && (
        <div className="card-body">
          {!!description && <p className="alert alert-info">{description}</p>}
          {(isService ||
            isServiceCategory ||
            isDate ||
            isPeriods ||
            isPeriod ||
            isStatus) && (
              <div className="row">
                {isServiceCategory && (
                  <div className="col-12 col-md mb-2">
                    <Select
                      openMenuOnFocus
                      noOptionsMessage={() => (
                        <span>داده ای برای نمایش یافت نشد</span>
                      )}
                      value={
                        service_category_id &&
                        categoryOptions?.find(
                          (obj) => service_category_id === obj.value
                        )
                      }
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={categoryOptions}
                      placeholder="دسته بندی خدمت"
                      onChange={(e) => {
                        const value = e?.value;
                        setService_category_id(value);
                      }}
                    />
                  </div>
                )}
                {isService && (
                  <div className="col-12 col-md mb-2">
                    <Select
                      openMenuOnFocus
                      noOptionsMessage={() => (
                        <span>داده ای برای نمایش یافت نشد</span>
                      )}
                      value={
                        unique_id &&
                        serviceOptions?.find((obj) => unique_id === obj.value)
                      }
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={serviceOptions}
                      placeholder="نوع خدمت"
                      onChange={(e) => {
                        const value = e?.value;
                        setUnique_id(value);
                      }}
                    />
                  </div>
                )}
                {isStatus && (
                  <div className="col-12 col-md mb-2">
                    <Select
                      openMenuOnFocus
                      noOptionsMessage={() => (
                        <span>داده ای برای نمایش یافت نشد</span>
                      )}
                      value={
                        status &&
                        statusOptions?.find((obj) => status === obj.value)
                      }
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={statusOptions}
                      placeholder="وضعیت"
                      onChange={(e) => {
                        const value = e?.value;
                        setStatus(value);
                      }}
                    />
                  </div>
                )}
                {isDate && (
                  <>
                    <div className="col-12 col-md mb-2">
                      <TooltipTitle title="از تاریخ">
                        <DateInput
                          id="from_date"
                          value={from_date}
                          className="form-control"
                          name="from_date"
                          onChange={(e) => setFrom_date(e.target.value)}
                          autoOk="false"
                          placeholder="از تاریخ"
                          dialogContainerStyle={dialogContainerStyle}
                        />
                      </TooltipTitle>
                    </div>
                    <div className="col-12 col-md mb-2">
                      <TooltipTitle title="تا تاریخ">
                        <DateInput
                          id="to_date"
                          value={to_date}
                          className="form-control"
                          name="to_date"
                          onChange={(e) => setTo_date(e.target.value)}
                          autoOk="false"
                          placeholder="تا تاریخ"
                          dialogContainerStyle={dialogContainerStyle}
                        />
                      </TooltipTitle>
                    </div>
                  </>
                )}
                {isPeriods && (
                  <div className="col-12 col-md mb-2">
                    <TooltipTitle title="دوره ها">
                      <input
                        value={periods.toString()}
                        onChange={(e) =>
                          setPeriods(e.target.value.split(",").map(Number))
                        }
                        className="form-control"
                      />
                    </TooltipTitle>
                  </div>
                )}
                {isPeriod && (
                  <div className="col-12 col-md mb-2">
                    <TooltipTitle title="دوره">
                      <input
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="form-control"
                      />
                    </TooltipTitle>
                  </div>
                )}
              </div>
            )}

          {algorithm === "geographic_distribution" ? (
            <>
              <Geographic data={data} />
            </>
          ) : algorithm === "returning_customer" ? (
            <>
              <p className="alert alert-primary">
                تعداد:{" "}
                <span className="font-weight-bold text-info">
                  {numberSeparate(data)}
                </span>
              </p>
            </>
          ) : algorithm === "revenue_total" ? (
            <div className="row">
              <div className="col-12 col-md">
                <p className="alert alert-primary">
                  جمع کل:{" "}
                  <span className="font-weight-bold text-info">
                    {numberSeparate(data.total__sum)}
                  </span>
                </p>
              </div>
              <div className="col-12 col-md">
                <p className="alert alert-primary">
                  درآمد:{" "}
                  <span className="font-weight-bold text-info">
                    {numberSeparate(data.revenue)}
                  </span>
                </p>
              </div>
            </div>
          ) : algorithm === "conversion_rate" ? (
            <div className="row">
              <div className="col-12 col-md">
                <p className="alert alert-primary">
                  کل کاربران:{" "}
                  <span className="font-weight-bold text-info">
                    {numberSeparate(data.total_users)}
                  </span>
                </p>
              </div>

              <div className="col-12 col-md">
                <p className="alert alert-primary">
                  کاربرانی که سفارش ثبت کردند:{" "}
                  <span className="font-weight-bold text-info">
                    {numberSeparate(data.users_with_orders)}
                  </span>
                </p>
              </div>
              <div className="col-12 col-md">
                <p className="alert alert-primary">
                  نرخ تبدیل:{" "}
                  <span className="font-weight-bold text-info">
                    {numberSeparate(data.conversion_rate)}
                  </span>
                </p>
              </div>
            </div>
          ) : algorithm === "orders_status" ? (
            <>
              <div className="row">
                <div className="col-12 col-md">
                  <p className="alert alert-primary">
                    سفارشات فیلتر شده:{" "}
                    <span className="font-weight-bold text-info">
                      {numberSeparate(data.total_filtered_orders)}
                    </span>
                  </p>
                </div>
                <div className="col-12 col-md">
                  <p className="alert alert-primary">
                    درصد:{" "}
                    <span className="font-weight-bold text-info">
                      {numberSeparate(
                        (
                          (data.total_filtered_orders /
                            data.total_total_orders) *
                          100
                        ).toFixed(2)
                      )}
                    </span>
                  </p>
                </div>
                <div className="col-12 col-md">
                  <p className="alert alert-primary">
                    کل سفارشات:{" "}
                    <span className="font-weight-bold text-info">
                      {numberSeparate(data.total_total_orders)}
                    </span>
                  </p>
                </div>
              </div>

              <Chart type={chartType} data={chartData} />
            </>
          ) : (
            <Chart type={chartType} data={chartData} />
          )}
        </div>
      )}
    </div>
  );
};

const Geographic = ({ data }) => {
  const mapRef = useRef(null);
  const clusterLayerRef = useRef(null);

  useEffect(() => {
    const vectorSource = new VectorSource();

    const clusterSource = new Cluster({
      distance: 40,
      source: vectorSource,
    });

    const clusterLayer = new VectorLayer({
      source: clusterSource,
      style: (feature) => {
        // const size = feature.get("features").length;
        const size = feature
          .get("features")
          .reduce((accu, current) => accu + current.values_.count, 0);

        return new Style({
          image: new CircleStyle({
            radius: 1 + Math.log(size) * 5,
            fill: new Fill({
              color: "rgba(0, 123, 255, 0.5)",
            }),
            stroke: new Stroke({
              color: "#007bff",
              width: 2,
            }),
          }),
          text: new Text({
            text: size.toString(),
            scale: 1.5,
            fill: new Fill({
              color: "#000",
            }),
            stroke: new Stroke({
              color: "#fff",
              width: 2,
            }),
          }),
        });
      },
    });

    const map = new Map({
      target: "map",
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
        clusterLayer,
      ],
      view: new View({
        center: fromLonLat([
          initialViewState.longitude,
          initialViewState.latitude,
        ]),
        zoom: 12,
      }),
    });

    mapRef.current = map;
    clusterLayerRef.current = clusterLayer;
  }, []);

  useEffect(() => {
    if (data && clusterLayerRef.current) {
      const features = data.map((item) => {
        const centerLat = (item.lat_end + item.lat_start) / 2;
        const centerLong = (item.long_end + item.long_start) / 2;

        const point = new Point(fromLonLat([centerLong, centerLat]));

        return new Feature({
          geometry: point,
          count: item.count,
        });
      });

      const vectorSource = clusterLayerRef.current.getSource().getSource();
      vectorSource.clear();
      vectorSource.addFeatures(features);
    }
  }, [data]);

  return <div id="map" style={{ width: "auto", height: "100vh" }} />;
};
